import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import Reveal from "@/components/motion/Reveal";
import PageHero from "@/components/PageHero";
import { T, useContent } from "@/content/ContentContext";

const IMG = "https://images.unsplash.com/photo-1759262151080-e05ba1c6294f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjbGluaWMlMjBpbnRlcmlvciUyMGx1eHVyeXxlbnwwfHx8fDE3ODc3MzY1Mzh8MA&ixlib=rb-4.1.0&q=85";

const VALUES = [
  { n: "01", k: "about.value.1" },
  { n: "02", k: "about.value.2" },
  { n: "03", k: "about.value.3" },
  { n: "04", k: "about.value.4" },
];

export default function AboutPage() {
  const { t, list } = useContent();
  return (
    <div data-testid="about-page">
      <PageHero
        overline={t("about.hero.overline")}
        lines={[<T key="1" k="about.hero.line1" />, <T key="2" k="about.hero.line2" em="text-brand-gold" />]}
        intro={t("about.hero.intro")}
        image={IMG}
        imageAlt="A calm modern clinic waiting room"
      />

      <section className="bg-brand-bone py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-brand-ink mt-4 mb-12" data-testid="story-heading">{t("about.founder.heading")}</h2>
              <div className="relative max-w-md">
                <div className="absolute -top-4 -left-4 h-full w-full border border-brand-gold/50" aria-hidden="true" />
                <img src="/natasha-bagier.jpg" alt="Natasha Bagier, founder of Mayfair Medical Services" className="relative w-full object-cover" data-testid="founder-portrait" />
                <div className="absolute -bottom-6 -right-4 sm:-right-8 bg-brand-green px-7 py-5">
                  <p className="font-serif text-xl text-brand-bone">{t("about.founder.name")}</p>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-brand-gold mt-1">{t("about.founder.caption")}</p>
                </div>
              </div>
              <div className="mt-20 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm" data-testid="founder-journey">
                {list("about.founder.journey").map((step, i) => (
                  <span key={step} className="flex items-center gap-6">
                    {i > 0 && <span className="hidden sm:inline h-1.5 w-1.5 rotate-45 bg-brand-gold" aria-hidden="true" />}
                    <span className="font-serif italic text-lg text-brand-ink/75">{step}</span>
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7 space-y-6 lg:pt-24">
            <Reveal delay={0.05}>
              <p className="font-serif text-2xl lg:text-3xl italic leading-snug text-brand-green" data-testid="story-quote">
                "{t("about.founder.quote")}"
              </p>
              <p className="mt-4 text-xs uppercase tracking-[0.25em] text-brand-gold">— {t("about.founder.name")}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-base lg:text-lg font-light leading-relaxed text-brand-ink/70">
                {t("about.founder.p1")}
              </p>
              <p className="mt-4 text-base lg:text-lg font-light leading-relaxed text-brand-ink/70">
                {t("about.founder.p2")}
              </p>
              <p className="mt-4 text-base lg:text-lg font-light leading-relaxed text-brand-ink/70">
                {t("about.founder.p3")}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-brand-deep text-brand-bone py-24 lg:py-32 texture-grain">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium mt-4" data-testid="values-heading">{t("about.values.heading")}</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-brand-bone/10 border border-brand-bone/10">
            {VALUES.map((v, i) => (
              <Reveal key={v.n} delay={i * 0.07} className="bg-brand-deep p-10 transition-colors duration-500 hover:bg-brand-pine/40">
                <span className="font-sans text-sm font-bold text-brand-gold tracking-[0.2em]">{v.n}</span>
                <h3 className="mt-4 font-serif text-2xl font-medium" data-testid={`value-title-${v.n}`}>{t(`${v.k}.title`)}</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-brand-bone/60">{t(`${v.k}.body`)}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-bone py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight font-medium text-brand-ink max-w-2xl leading-[1.05]" data-testid="about-cta-heading">
              {t("about.cta.heading")}
            </h2>
            <Link to="/contact" data-testid="about-cta-button" className="mt-10 inline-flex items-center gap-3 rounded-full bg-brand-green px-9 py-4 text-sm font-semibold text-brand-bone transition-all duration-300 hover:bg-brand-pine hover:-translate-y-0.5">
              {t("about.cta.button")} <ArrowRight size={16} weight="bold" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
